import { memo, useEffect, useState } from 'react'
import { Button } from 'antd'
import { Download, Plus, ShieldCheck, WalletCards } from 'lucide-react'
import WalletSwapper from '@/components/WalletSwapper'
import { Notification } from '@/components'
import exportToExcel from '@/utils/downExcel'
import { Page } from '@/styles'
import { useAccounIsMumber } from '@/hooks'
import { CreateWalletSwapper, EmptyState, Header, ResultHeader } from './style'
import { generateMnemonic, english, mnemonicToAccount } from 'viem/accounts'
import { Input } from '@/components/Input'
import { toHex } from 'viem'

export interface WalletType {
  address: string
  mnemonic: string
  privateKey: string
}

function CreateWallet() {
  useAccounIsMumber()
  const [walletNumber, setWalletNumber] = useState('')
  const [walletArr, setWalletArr] = useState<WalletType[]>([])
  const [isCreate, setIsCreate] = useState(false)
  const [targetCount, setTargetCount] = useState(0)

  useEffect(() => {
    if (!isCreate) return
    if (walletArr.length >= targetCount) {
      Notification.Success('钱包创建完成，请及时安全保存')
      setIsCreate(false)
      return
    }

    const timer = window.setTimeout(() => {
      const mnemonic = generateMnemonic(english)
      const account = mnemonicToAccount(mnemonic)
      const privateKeyBytes = account.getHdKey().privateKey
      if (!privateKeyBytes) throw new Error('获取私钥失败')
      setWalletArr((items) => [...items, {
        address: account.address,
        mnemonic,
        privateKey: toHex(privateKeyBytes),
      }])
    }, 0)

    return () => window.clearTimeout(timer)
  }, [isCreate, targetCount, walletArr.length])

  const createWalletMiddler = () => {
    const count = Number(walletNumber)
    if (!Number.isInteger(count) || count < 1 || count > 1000) {
      Notification.Error('请输入 1 至 1000 之间的整数')
      return
    }
    setIsCreate(true)
    setTargetCount(count)
    setWalletArr([])
  }

  const downWalletHandler = () => {
    if (walletArr.length === 0) return Notification.Error('请先生成钱包')
    exportToExcel(walletArr)
  }

  return (
    <Page>
      <CreateWalletSwapper>
        <Header>
          <div className="hero">
            <div className="hero-icon"><WalletCards size={23} /></div>
            <div>
              <h1>批量生成钱包</h1>
              <p className="subtitle">一次生成多个独立钱包，并将重要信息安全导出保存。</p>
            </div>
          </div>
          <div className="createWallet">
            <Input className="creatInput" placeholder="请输入创建数量（1 - 1000）" value={walletNumber} onChange={(event) => setWalletNumber(event.target.value)} type="number" min="1" max="1000" />
            <Button className="create-btn" icon={<Plus size={17} />} loading={isCreate} onClick={createWalletMiddler}>开始生成</Button>
            <Button className="export-btn" icon={<Download size={16} />} disabled={walletArr.length === 0} onClick={downWalletHandler}>导出表格</Button>
          </div>
          <div className="notice"><ShieldCheck size={16} />请在可信、离线的环境生成并保存助记词和私钥，切勿向他人透露。</div>
        </Header>

        <ResultHeader>
          <h2>生成结果</h2>
          <span>{walletArr.length > 0 ? `已生成 ${walletArr.length} 个钱包` : '尚未生成钱包'}</span>
        </ResultHeader>
        {walletArr.length === 0
          ? <EmptyState>输入需要生成的钱包数量，然后开始创建。</EmptyState>
          : walletArr.map((wallet, index) => <WalletSwapper key={wallet.address} wallet={wallet} index={index + 1} />)}
      </CreateWalletSwapper>
    </Page>
  )
}

export default memo(CreateWallet)
